import React from 'react';
import {
  Badge, Button, Modal,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsCardList } from 'react-icons/bs';
import {
  getLogsList,
  getResponseBody,
  stringifySafe,
} from 'screens/Admin/Accounts/components/syncSolicitudDocModalHelpers';

type SyncSolicitudDocLogsModalProps = {
  show: boolean;
  selectedAccountId: number | null;
  runId?: string | null;
  logsResponse: any;
  showRawFooter?: boolean;
  onClose: () => void;
};

function SyncSolicitudDocLogsModal( {
  show,
  selectedAccountId,
  runId,
  logsResponse,
  showRawFooter = true,
  onClose,
}: SyncSolicitudDocLogsModalProps ) {
  const { t } = useTranslation();
  const logsBody = getResponseBody( logsResponse );
  const logsItems = getLogsList( logsBody );

  return (
    <Modal show={show} centered size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <BsCardList className="me-2" />
          {t( 'admin.account.syncSolicitudDocLogsTitle' )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          {t( 'admin.account.syncSolicitudDocAccountLabel' )}
          {' '}
          <strong>{selectedAccountId ?? '-'}</strong>
        </p>
        {runId ? (
          <p className="mb-3 text-muted small">
            {t( 'admin.account.syncSolicitudDocRunIdLabel' )}
            {' '}
            <code>{runId}</code>
          </p>
        ) : (
          <p className="mb-3" />
        )}
        <div
          className="bg-light p-3 rounded"
          style={{
            maxHeight: '55vh',
            overflow: 'auto',
          }}
        >
          {!logsResponse && (
            <p className="mb-0 text-muted">{t( 'admin.account.syncSolicitudDocNoResponse' )}</p>
          )}

          {logsResponse && logsItems.length === 0 && (
            <>
              <p className="mb-2 text-muted">{t( 'admin.account.syncSolicitudDocNoEvents' )}</p>
              {showRawFooter ? (
                <pre
                  className="bg-white p-2 rounded mb-0"
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '28vh',
                    overflow: 'auto',
                  }}
                >
                  {stringifySafe( logsBody ) || '-'}
                </pre>
              ) : null}
            </>
          )}

          {logsItems.length > 0 && (
            <>
              {logsItems.map( ( item:any ) => (
                <div
                  key={`${item?.id ?? ''}-${item?.eventType ?? ''}-${item?.createdAt ?? ''}-${item?.message ?? ''}`}
                  className="border-bottom pb-2 mb-2"
                >
                  <div className="d-flex align-items-center mb-1">
                    <Badge bg={item?.errorCode || item?.errorMessage ? 'danger' : 'secondary'}>
                      {item?.eventType ?? 'EVENT'}
                    </Badge>
                    <small className="text-muted ms-2">{String( item?.createdAt ?? item?.eventDate ?? '' )}</small>
                  </div>
                  <div>
                    <strong>categoria:</strong>
                    {' '}
                    {String( item?.categoria ?? '-' )}
                  </div>
                  <div>
                    <strong>mensaje:</strong>
                    {' '}
                    {String( item?.message ?? item?.lastMessage ?? '-' )}
                  </div>
                  {( item?.errorCode || item?.errorMessage ) && (
                    <div className="text-danger">
                      <strong>error:</strong>
                      {' '}
                      {String( item?.errorCode ?? '' )}
                      {' '}
                      {String( item?.errorMessage ?? '' )}
                    </div>
                  )}
                </div>
              ) )}
              {showRawFooter ? (
                <>
                  <h6 className="mt-3 mb-2">{t( 'admin.account.syncSolicitudDocRawLogs' )}</h6>
                  <pre
                    className="bg-white p-2 rounded mb-0"
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: '30vh',
                      overflow: 'auto',
                    }}
                  >
                    {stringifySafe( logsBody ) || '-'}
                  </pre>
                </>
              ) : null}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          {t( 'close' )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SyncSolicitudDocLogsModal;
